/**
 * Configuration Loader
 *
 * This module handles loading configuration from external config.js file.
 * The config.js file is NOT bundled with the app and must be deployed separately.
 * This ensures API keys are not exposed in the compiled JavaScript bundles.
 */

import type { AppConfig } from '../types';

declare global {
  interface Window {
    appConfig?: AppConfig;
  }
}

/**
 * Load configuration from external config.js file
 *
 * This function expects a config.js file to be loaded before the app starts.
 * The config.js should define window.appConfig with all necessary configuration.
 *
 * @throws Error if configuration is not found
 */
export async function loadConfig(): Promise<AppConfig> {
  // Wait a bit for config.js to load (if loaded via script tag)
  await new Promise(resolve => setTimeout(resolve, 100));

  if (!window.appConfig) {
    throw new Error(
      'Configuration not found. Please ensure config.js is loaded and contains window.appConfig'
    );
  }

  // Validate required fields
  validateConfig(window.appConfig);

  return window.appConfig;
}

/**
 * Validate that all required configuration fields are present
 */
function validateConfig(config: AppConfig): void {
  const errors: string[] = [];

  // Validate Firebase config
  if (!config.firebase) {
    errors.push('Firebase configuration is missing');
  } else {
    if (!config.firebase.apiKey) errors.push('Firebase API key is missing');
    if (!config.firebase.authDomain) errors.push('Firebase auth domain is missing');
    if (!config.firebase.projectId) errors.push('Firebase project ID is missing');
  }

  // Validate AI config
  if (!config.ai) {
    errors.push('AI configuration is missing');
  } else {
    if (!config.ai.model) errors.push('AI model is missing');
    if (!config.ai.maxTokens) errors.push('AI maxTokens is missing');
    if (!config.ai.sykehjemPromptTemplate) errors.push('Sykehjem prompt template is missing');
    if (!config.ai.boligPromptTemplate) errors.push('Bolig prompt template is missing');
  }

  if (errors.length > 0) {
    throw new Error(
      `Configuration validation failed:\n${errors.join('\n')}\n\nPlease check your config.js file.`
    );
  }
}

/**
 * Get current configuration (throws if not loaded yet)
 */
export function getConfig(): AppConfig {
  if (!window.appConfig) {
    throw new Error('Configuration not loaded. Call loadConfig() first.');
  }
  return window.appConfig;
}

/**
 * Update AI settings (used by settings page)
 */
export function updateAISettings(settings: Partial<AppConfig['ai']>): void {
  if (!window.appConfig) {
    throw new Error('Configuration not loaded');
  }
  window.appConfig.ai = { ...window.appConfig.ai, ...settings };
}
